import React, { useState } from 'react';
import { Settings, LogOut, UserCircle } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';

interface AccountDropdownProps {
  onSettingsClick: () => void;
}

const AccountDropdown: React.FC<AccountDropdownProps> = ({ onSettingsClick }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  
  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user) return '?';
    
    // Try to generate initials from first and last name
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`;
    }
    
    // If no name is available, use the first letter of username or email
    if (user.username) {
      return user.username.charAt(0).toUpperCase();
    }
    
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    
    return '?';
  };
  
  const handleSettingsClick = () => {
    setOpen(false);
    onSettingsClick();
  };
  
  const handleLogout = () => {
    setOpen(false);
    logout();
  };
  
  if (!user) return null;
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative h-10 w-10 rounded-full border-2 border-indigo-400 dark:border-indigo-500 p-0 hover:bg-indigo-50 dark:hover:bg-indigo-950"
        >
          <Avatar>
            <AvatarFallback>{getUserInitials()}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 border-2 border-indigo-400 dark:border-indigo-500">
        <DropdownMenuLabel className="font-semibold text-indigo-600 dark:text-indigo-400">
          {user?.username || user?.email || 'User'}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            
            // Log the navigation attempt
            console.log('🚀 Navigating to profile page');
            
            // Force a full page reload - this is a guaranteed solution
            // It's not the most elegant React solution, but it will ensure the page loads
            window.location.href = '/profile?_t=' + Date.now();
          }}
          className="cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950 text-foreground"
        >
          <UserCircle className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-950 text-foreground">
          <button type="button" onClick={handleSettingsClick} className="flex items-center w-full text-left">
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </button>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={handleLogout}
          className="cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-950 text-rose-600 dark:text-rose-400"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountDropdown; 